import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { FoldList } from '../../../../shared/table-builder/fold-list'
import { Menu, MenuItem, MenuEdit, MenuCopy, MenuDelete } from '../../../../shared/menu/menu'
import { IconAdd, IconList } from '../../../../shared/icon/icon'
import { useAlertRoutingAccess } from './access'
import { listRoutes, routeState, deleteRoute, createRoute } from './alert-routing-api'
import { useCatalog } from './catalog'
import { triggerParts, filterItems, whenItems, NO_FILTERS } from './route-summary'
import { toBody } from './route-body'
import { SETUP_PILL, routeSetup } from './setup-words'
import { typeTitle } from './target-words'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { ROUTES_PAGE, routeHref } from './paths'

// The State column (spec §20.2): an On/Off switch. Paused sits beside the
// route's name. A click on the switch is the switch's, never the row's (the
// grid's shared rule, table-builder/row-click.js); the word beside it opens
// the route.
const StateSwitch = ({ route, canEdit, onToggle }) =>
  <span className="ar-state">
    <span className="form-check form-switch m-0">
      <input className="form-check-input" type="checkbox" role="switch" aria-label={`${route.name} on`} checked={Boolean(route.enabled)}
        disabled={!canEdit} onChange={onToggle} />
    </span>
    <span>{route.enabled ? 'On' : 'Off'}</span>
  </span>

// Two-line cells, like the targets list, so the list fits a 1280 px window
// (owner 2026-11-02: it scrolled sideways): the route over its scope; its
// alerts over its filters in words; its targets over when it sends. The caps
// keep every column in the grid at 1280 px however long the text (954 px:
// the three caps, Last matched, State and two row buttons), measured on dev.
// A list longer than its cap shows the whole items that fit and "+2 more"
// (or "+2" where that leaves more room for a word), which opens all of them;
// a category-wide route shows its set and "except 1" (fold-list.js; owner
// 2026-11-02: never cut off).
export const ROUTE_MAX = 200
export const ALERTS_MAX = 230
export const SENDS_MAX = 200
const scopeText = route => (route.scopeType === 'platform' ? 'Platform' : route.organizationName || `Organization ${route.organizationID}`)
export const sendsToItems = (route, catalog) => (route.actionTargets || [])
  .map(t => `${t.name} · ${typeTitle(catalog.actionTypes.find(a => a.type === t.type)) || t.type}`)
const routeCell = r => (r.data
  ? <span className="min-w-0">
      <span className="tb-cell-row">
        <span className="fw-semibold text-truncate" title={r.data.name}>{r.data.name}</span>
        {r.data.enabled && r.data.paused && <Pill tone="warn">Paused</Pill>}
        {routeSetup(r.data) && <Pill tone="warn" title="Sends nowhere yet.">{SETUP_PILL}</Pill>}
      </span>
      <span className="sw-cell-sub text-truncate">{scopeText(r.data)}</span>
    </span>
  : '')

// Alert routes (/admin/alert-routing/routes; owner 2026-10-30: its own page in
// the sidebar). The house list page (CLAUDE.md "List page layout"): the
// category filter in the toolbar row (in the address, ?category=, as the
// Errors and SSO pages link to it), then the card, whose menu has New route
// pinned. A route with no target switched on says "Setup incomplete" beside
// its name (setup-words.js). A click on a row opens the route, so its row
// pins only Delete, with the rest in its ⋮ (as the targets list pins two);
// Delete asks first in the house confirmation, with the scrim.
export const RoutesPage = () => {
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const history = useHistory()
  const category = new URLSearchParams(useLocation().search).get('category') || ''
  const [rows, setRows] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [deleting, setDeleting] = useState(null)
  const [busy, setBusy] = useState(false)
  const [deleteError, deleteFailure, setDeleteError] = useErrorLine()
  const tableRef = useRef()
  const reload = () => listRoutes(category).then(setRows, err => setError(serverMessage(err, "Routes couldn't be loaded."), err))
  useEffect(() => { reload() }, [category]) // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (r, fn) => { try { await fn(); reload() } catch (err) { setError(serverMessage(err, "That didn't work."), err) } }
  const getRows = useMemo(() => async (start, end) => (rows ? { data: rows.slice(start, end), count: rows.length } : { data: [], count: 0 }), [rows])
  const pickCategory = value => history.push(value ? `${ROUTES_PAGE}?category=${encodeURIComponent(value)}` : ROUTES_PAGE)
  const remove = async () => {
    if (busy || !deleting) return
    setBusy(true)
    setDeleteError(null)
    try {
      await deleteRoute(deleting.id)
      setDeleting(null)
      reload()
    } catch (err) {
      setDeleteError(serverMessage(err, "The route couldn't be deleted."), err)
    } finally {
      setBusy(false)
    }
  }
  const menu = canEdit
    ? <MenuItem key="new" Icon={IconAdd} text="New route" pin keepOnMobile show to={`${ROUTES_PAGE}/new?category=${category || 'errors'}`} />
    : []
  return (
    <FleetPage title="Alert routes" purpose="Which alerts go to which targets, and when.">
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
      {catalog.categories.length > 0 &&
        <div className="sw-toolbar" role="group" aria-label="Filter the routes">
          <select aria-label="Category" className="form-select form-select-sm" value={category} onChange={e => pickCategory(e.target.value)}>
            <option value="">Every category</option>
            {catalog.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>}
      {rows && rows.map(r => canEdit && (
        <span key={`q${r.id}`} className="visually-hidden">
          <button type="button" tabIndex={-1} aria-label={`${r.paused ? 'Resume' : 'Pause'} ${r.name}`} onClick={() => act(r, () => routeState(r.id, r.paused ? 'resume' : 'pause'))} />
        </span>
      ))}
      <FleetGrid title="Routes" tableRef={tableRef} menu={menu} rows={(rows || []).length} rowHeight={ROW_HEIGHT_TWO_LINE}>
        <TableAgInfinite key={rows ? rows.length + JSON.stringify(rows.map(r => [r.id, r.paused, r.enabled])) : 'x'} ref={tableRef} queryKey="" onGetRows={getRows}
          rowHeight={ROW_HEIGHT_TWO_LINE} onRowClicked={r => history.push(routeHref(r.id))}
          emptyState={{ Icon: IconList, title: 'No routes', message: 'Nothing is routed here yet.' }}>
          <ColumnFormat field="name" header="Route" sortable={false} stacked flex={1.5} textMax={ROUTE_MAX} render={routeCell} />
          <ColumnFormat field="category" header="Alerts" sortable={false} stacked flex={2} textMax={ALERTS_MAX} render={r => (r.data
            ? <span className="min-w-0">
                <span><FoldList {...triggerParts(r.data, catalog)} title="Alerts" /></span>
                <span className="sw-cell-sub"><FoldList items={filterItems(r.data, catalog)} separator=" · " title="Filters" empty={NO_FILTERS} /></span>
              </span>
            : '')} />
          <ColumnFormat field="actionTargets" header="Sends to" sortable={false} stacked flex={1.5} textMax={SENDS_MAX} render={r => (r.data
            ? <span className="min-w-0">
                <span><FoldList items={sendsToItems(r.data, catalog)} title="Sends to" empty="Nowhere yet" /></span>
                <span className="sw-cell-sub"><FoldList items={whenItems(r.data)} separator=" · " title="When" /></span>
              </span>
            : '')} />
          <ColumnFormat field="lastMatchedAt" header="Last matched" sortable={false} fit="content" render={r => (r.data ? (r.value ? formatAgo(r.value) : 'Never') : '')} />
          <ColumnFormat field="enabled" header="State" sortable={false} fit="content" render={r => (r.data
            ? <StateSwitch route={r.data} canEdit={canEdit}
              onToggle={() => act(r.data, () => routeState(r.data.id, r.data.enabled ? 'disable' : 'enable'))} />
            : '')} />
          {canEdit && <ColumnMenu menu={r => (
            <Menu small>
              <MenuEdit pin={false} show onClick={() => history.push(routeHref(r.id))} />
              <MenuItem text="test" show onClick={() => history.push(`${routeHref(r.id)}?step=5`)} />
              <MenuItem text={r.paused ? 'resume' : 'pause'} show onClick={() => act(r, () => routeState(r.id, r.paused ? 'resume' : 'pause'))} />
              <MenuItem text={r.enabled ? 'turn off' : 'turn on'} show onClick={() => act(r, () => routeState(r.id, r.enabled ? 'disable' : 'enable'))} />
              <MenuCopy text="duplicate" pin={false} show onClick={() => act(r, () => createRoute({ ...toBody({ ...r, name: `${r.name} (copy)` }) }))} />
              <MenuDelete show onClick={() => { setDeleteError(null); setDeleting(r) }} />
            </Menu>)} />}
        </TableAgInfinite>
      </FleetGrid>
      <ConfirmDialog open={Boolean(deleting)} scrim title={deleting ? `Delete the route "${deleting.name}"?` : ''} confirmText="Delete route" busy={busy}
        body="Alerts it would have sent stop now. This can't be undone." error={deleteError} failure={deleteFailure}
        onCancel={() => setDeleting(null)} onConfirm={remove} />
    </FleetPage>
  )
}
